import React, { useState, useEffect } from 'react';
import { useCountdown } from '../hooks/useCountdown';
import { X, Mail, Smartphone, ArrowRight, Loader2, Gift, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '../i18nContext';
import { sendVerificationCode, sendSmsCode, register, registerWithPhone } from '../services/auth';
import BrandLogo from './BrandLogo';
import { getErrorMessage } from '../utils/error';

interface SignUpModalProps {
    onClose: () => void;
    onSignUp: () => void;
    onSwitchToLogin: () => void;
    initialInviteCode?: string;
    prefill?: { method: 'email' | 'phone'; contact: string; code: string } | null;
}

export default function SignUpModal({ onClose, onSignUp, onSwitchToLogin, initialInviteCode = '', prefill }: SignUpModalProps) {
    const [method, setMethod] = useState<'email' | 'phone'>(prefill?.method || 'email');
    const [testimonialIdx, setTestimonialIdx] = useState(0);
    const { t } = useLanguage();

    const isFromLogin = !!prefill;
    const lockCode = isFromLogin && !!prefill?.code;

    // 表单状态
    const [fullName, setFullName] = useState('');
    const [emailOrPhone, setEmailOrPhone] = useState(prefill?.contact || '');
    const [verificationCode, setVerificationCode] = useState(prefill?.code || '');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [inviteCode, setInviteCode] = useState(initialInviteCode);

    // 验证码发送状态
    const [sendingCode, setSendingCode] = useState(false);
    const [countdown, startCountdown] = useCountdown(60);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [isShaking, setIsShaking] = useState(false);

    const testimonials = t.auth.testimonials;

    // 发送验证码
    const handleSendCode = async () => {
        if (!emailOrPhone.trim()) {
            setError(method === 'email' ? t.auth.enterEmail : t.auth.enterPhone);
            return;
        }
        if (countdown > 0 || sendingCode) return;

        setSendingCode(true);
        setError('');
        try {
            if (method === 'email') {
                await sendVerificationCode(emailOrPhone);
            } else {
                await sendSmsCode(emailOrPhone, 'register');
            }
            startCountdown();
        } catch (error: unknown) {
            setError(getErrorMessage(error, t.auth.codeSendFailed));
        } finally {
            setSendingCode(false);
        }
    };

    // 提交注册
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!fullName.trim() || !emailOrPhone.trim() || !verificationCode.trim() || !password.trim()) {
            setError(t.auth.fillAllRequired);
            setIsShaking(true);
            setTimeout(() => setIsShaking(false), 500);
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            if (method === 'email') {
                await register({
                    email: emailOrPhone,
                    password,
                    username: fullName,
                    verificationCode,
                    inviteCode: inviteCode.trim() || undefined,
                });
            } else {
                await registerWithPhone({
                    phone: emailOrPhone,
                    code: verificationCode,
                    password,
                    username: fullName,
                    inviteCode: inviteCode.trim() || undefined,
                });
            }
            onSignUp();
        } catch (error: unknown) {
            setIsShaking(true);
            setTimeout(() => setIsShaking(false), 500);
            setError(getErrorMessage(error, t.auth.registrationFailed));
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (testimonials.length <= 1) {
            setTestimonialIdx(0);
            return;
        }

        const timer = setInterval(() => {
            setTestimonialIdx((prev) => (prev + 1) % testimonials.length);
        }, 6000);

        return () => clearInterval(timer);
    }, [testimonials.length]);

    // 共享的表单字段渲染
    const renderFormFields = (currentType: 'email' | 'phone') => (
        <>
            <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-ink-muted uppercase tracking-widest ml-1 block">{t.auth.fullName}</label>
                <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full h-11 px-4 rounded-lg border border-surface-border bg-surface-DEFAULT text-sm font-sans font-semibold focus:bg-white focus:border-surface-divider focus:ring-1 focus:ring-brand-700 transition-all outline-none"
                />
            </div>
            <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-ink-muted uppercase tracking-widest ml-1 block">
                    {currentType === 'email' ? t.auth.email : t.auth.mobileNumber}
                </label>
                <input
                    type={currentType === 'email' ? 'email' : 'tel'}
                    value={emailOrPhone}
                    onChange={(e) => !isFromLogin && setEmailOrPhone(e.target.value)}
                    readOnly={isFromLogin}
                    className={`w-full h-11 px-4 rounded-lg border border-surface-border text-sm font-sans font-semibold transition-all outline-none ${isFromLogin ? 'bg-surface-DEFAULT text-ink-muted cursor-not-allowed' : 'bg-surface-DEFAULT focus:bg-white focus:border-surface-divider focus:ring-1 focus:ring-brand-700'}`}
                />
            </div>
            <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-ink-muted uppercase tracking-widest ml-1 block">{t.auth.verificationCode}</label>
                <div className="flex gap-3">
                    <input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => !lockCode && setVerificationCode(e.target.value)}
                        readOnly={lockCode}
                        className={`flex-1 h-11 px-4 rounded-lg border border-surface-border text-sm font-sans font-semibold transition-all outline-none ${lockCode ? 'bg-surface-DEFAULT text-ink-muted cursor-not-allowed' : 'bg-surface-DEFAULT focus:bg-white focus:border-surface-divider focus:ring-1 focus:ring-brand-700'}`}
                    />
                    {!lockCode && (
                        <button
                            type="button"
                            onClick={handleSendCode}
                            disabled={sendingCode || countdown > 0}
                            className="px-4 h-11 bg-surface-DEFAULT border border-surface-border rounded-lg text-sm font-bold text-brand-900 hover:bg-surface-border disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap min-w-[120px] flex items-center justify-center"
                        >
                            {sendingCode ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : countdown > 0 ? (
                                `${countdown}s`
                            ) : (
                                t.auth.sendCode
                            )}
                        </button>
                    )}
                </div>
            </div>
            <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-ink-muted uppercase tracking-widest ml-1 block">{t.auth.password}</label>
                <div className="relative">
                    <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full h-11 px-4 pr-12 rounded-lg border border-surface-border bg-surface-DEFAULT text-sm font-sans font-semibold focus:bg-white focus:border-surface-divider focus:ring-1 focus:ring-brand-700 transition-all outline-none"
                    />
                    {password.length > 0 && (
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-muted hover:text-brand-900 transition-colors"
                        >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    )}
                </div>
            </div>
            <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-ink-muted uppercase tracking-widest ml-1 flex items-center gap-1.5">
                    <Gift size={10} className="text-orange-400" />
                    {t.auth.inviteCode}
                </label>
                <input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    className="w-full h-11 px-4 rounded-lg border border-surface-border bg-orange-50 text-sm font-sans font-semibold focus:bg-white focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition-all outline-none font-mono tracking-wider"
                />
                {inviteCode && (
                    <p className="text-xs text-orange-600 ml-1">
                        {t.auth.inviteCodeHint}
                    </p>
                )}
            </div>
        </>
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 isolate">
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm transform-gpu transition-opacity duration-300 pointer-events-auto"
                style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                onClick={onClose}
            ></div>

            <div className="relative w-full max-w-5xl bg-white rounded-modal shadow-modal overflow-hidden flex flex-col md:flex-row h-auto max-h-[95vh] animate-in fade-in zoom-in-95 duration-500 border border-white z-10">

                {/* Left Side: Testimonial Panel */}
                <div className="w-full md:w-[45%] bg-surface-light p-6 md:px-10 md:py-8 flex flex-col relative overflow-hidden shrink-0 border-r border-surface-border">
                    <div className="absolute top-[-50px] left-[-50px] w-80 h-80 bg-brand-200/30 rounded-full blur-[80px]"></div>
                    <div className="absolute bottom-[-50px] right-[-50px] w-80 h-80 bg-prestige-300/20 rounded-full blur-[80px]"></div>
                    <div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none"></div>

                    <div className="relative z-10 mb-auto">
                        <BrandLogo variant="light" className="mb-6" />
                    </div>

                    <div className="relative z-10 flex-1 flex flex-col justify-center">
                        <div className="relative min-h-[320px]">
                            {testimonials.map((testimonial, idx) => (
                                <div
                                    key={idx}
                                    className={`absolute inset-0 transition-all duration-1000 ease-in-out flex flex-col justify-center text-center
                                ${idx === testimonialIdx ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
                                >
                                    <p className="text-2xl md:text-3xl font-serif font-bold text-brand-900 leading-snug mb-10 px-4">
                                        &ldquo;{testimonial.text}&rdquo;
                                    </p>

                                    <div className="flex flex-col items-center">
                                        <div className="w-20 h-20 rounded-full border-4 border-white shadow-xl overflow-hidden mb-4">
                                            <img src={testimonial.avatar} alt={testimonial.name} className="w-full h-full object-cover" />
                                        </div>
                                        <div className="space-y-1">
                                            <div className="text-base font-bold text-brand-900">{testimonial.name}</div>
                                            <div className="text-xs font-normal text-ink-secondary opacity-70 uppercase tracking-widest">{testimonial.role}</div>
                                            <div className="text-xs font-bold text-brand-700">{testimonial.company}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="relative z-10 mt-auto pt-8">
                        <div className="flex justify-center gap-2">
                            {testimonials.map((_, idx) => (
                                <div key={idx} className={`h-1.5 rounded-full transition-all duration-300 ${idx === testimonialIdx ? 'w-8 bg-brand-900' : 'w-2 bg-surface-border'}`}></div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Side: Form */}
                <div className="flex-1 p-6 md:px-10 md:py-8 bg-white flex flex-col justify-start relative overflow-y-auto">
                    <button onClick={onClose} className="absolute top-5 right-5 p-2 text-ink-muted hover:text-brand-900 rounded-full hover:bg-surface-hover transition-all z-20">
                        <X size={20} />
                    </button>

                    <div className="mb-4 text-center md:text-left mt-0">
                        <h2 className="text-4xl font-serif font-bold text-brand-900 mb-2">{t.auth.createAccount}</h2>
                        <p className="text-ink-muted text-sm font-light">{t.auth.createSub}</p>
                    </div>

                    <div className="flex p-1 bg-surface-DEFAULT rounded-full mb-6 border border-surface-border">
                        <button type="button" onClick={() => { if (!isFromLogin && method !== 'email') { setMethod('email'); setError(''); } }} className={`flex-1 py-3 text-xs font-semibold tracking-wide rounded-full transition-all flex items-center justify-center gap-1.5 ${method === 'email' ? 'bg-white text-brand-900 shadow-sm ring-1 ring-surface-border' : 'text-ink-muted hover:text-brand-900'} ${isFromLogin ? 'cursor-not-allowed' : ''}`}>
                            <Mail size={14} /> {t.auth.email}
                        </button>
                        <button type="button" onClick={() => { if (!isFromLogin && method !== 'phone') { setMethod('phone'); setError(''); } }} className={`flex-1 py-3 text-xs font-semibold tracking-wide rounded-full transition-all flex items-center justify-center gap-1.5 ${method === 'phone' ? 'bg-white text-brand-900 shadow-sm ring-1 ring-surface-border' : 'text-ink-muted hover:text-brand-900'} ${isFromLogin ? 'cursor-not-allowed' : ''}`}>
                            <Smartphone size={14} /> {t.auth.phone}
                        </button>
                    </div>

                    <div className={`transition-all duration-300 overflow-hidden relative z-20 ${error ? 'max-h-16 opacity-100 mb-4' : 'max-h-0 opacity-0 mb-0'}`}>
                        <div className="flex items-center gap-2 p-2.5 bg-brand-900 border border-transparent rounded-xl text-surface-light text-xs font-bold tracking-wide shadow-lg">
                            <AlertCircle size={14} className="shrink-0" />
                            {error}
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="relative grid grid-cols-1">
                        {/* Wrapper for fields to animate sliding */}
                        <div className={`col-start-1 row-start-1 flex flex-col gap-3 transition-all duration-300 ease-in-out transform ${method === 'email' ? 'opacity-100 translate-x-0 z-10 pointer-events-auto delay-100' : 'opacity-0 -translate-x-8 z-0 pointer-events-none'}`}>
                            {renderFormFields('email')}
                        </div>
                        <div className={`col-start-1 row-start-1 flex flex-col gap-3 transition-all duration-300 ease-in-out transform ${method === 'phone' ? 'opacity-100 translate-x-0 z-10 pointer-events-auto delay-100' : 'opacity-0 translate-x-8 z-0 pointer-events-none'}`}>
                            {renderFormFields('phone')}
                        </div>

                        <div className="col-start-1 row-start-2 pt-4 mt-2">
                            <button
                                type="submit"
                                disabled={submitting}
                                className={`w-full relative h-12 ${isShaking ? 'animate-shake bg-brand-900 shadow-lg' : 'bg-brand-600 hover:bg-brand-700 shadow-cta-dark hover:shadow-2xl hover:-translate-y-0.5'} text-surface-light rounded-full text-lg font-medium tracking-wide transition-all duration-300 group flex items-center justify-center gap-3 overflow-hidden disabled:opacity-70 disabled:cursor-not-allowed`}
                            >
                                <div className="absolute inset-0 bg-noise opacity-10 mix-blend-overlay"></div>
                                {submitting ? (
                                    <Loader2 size={20} className="animate-spin relative z-10" />
                                ) : (
                                    <>
                                        <span className="relative z-10">{t.auth.signup}</span>
                                        <ArrowRight size={18} className="relative z-10 group-hover:translate-x-1 transition-transform stroke-[2.5]" />
                                    </>
                                )}
                            </button>
                        </div>
                    </form>

                    <div className="mt-8 text-center">
                        <p className="text-ink-secondary text-sm font-light">
                            {t.auth.hasAccount}{' '}
                            <button onClick={onSwitchToLogin} className="text-brand-900 font-semibold hover:underline ml-1">
                                {t.auth.login}
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

